// src/components/ecommerce/ServiceOptionsModal.jsx - CORRECT VERSION WITH FREE QUANTITY LOGIC

import React from 'react';
import {
  X,
  Plus,
  Minus,
  AlertCircle,
  CheckCircle,
  Clock,
  MapPin,
  Gift
} from 'lucide-react';

export const ServiceOptionsModal = ({
  service,
  selectedOptions,
  onOptionChange,
  onClose,
  onBook,
  styling = {}
}) => {
  console.log('🎬 ServiceOptionsModal rendering:', {
    serviceName: service.name,
    optionGroups: service.optionGroups,
    selectedOptions
  });

  // ✅ CORRECT: Calculate price with freeQuantity logic
  const calculateTotalPrice = () => {
    let total = parseFloat(service.price) || 0;
    
    if (service.optionGroups) {
      service.optionGroups.forEach((group, groupIndex) => {
        // Skip if all options in this group are free
        if (group.allFree) return;
        
        // Count total selections in this group
        const groupSelections = [];
        group.options?.forEach((option, optionIndex) => {
          const key = `${groupIndex}-${optionIndex}`;
          const quantity = selectedOptions[key] || 0;
          
          for (let i = 0; i < quantity; i++) {
            groupSelections.push({
              option,
              optionIndex,
              key
            });
          }
        });
        
        // Sort by price (ascending) to apply free quantity to cheapest items first
        groupSelections.sort((a, b) => (a.option.price || 0) - (b.option.price || 0));
        
        // Apply pricing: first X are free, rest are charged
        const freeQuantity = group.freeQuantity || 0;
        groupSelections.forEach((selection, index) => {
          if (index >= freeQuantity && !selection.option.isFree) {
            total += selection.option.price || 0;
          }
        });
      });
    }
    
    return total.toFixed(2);
  };

  // ✅ Calculate how many free options remain in each group
  const getFreeOptionsRemaining = (groupIndex) => {
    const group = service.optionGroups[groupIndex];
    if (!group || group.allFree) return Infinity;
    
    const freeQuantity = group.freeQuantity || 0;
    if (freeQuantity === 0) return 0;
    
    // Count total selections in this group
    let totalSelected = 0;
    group.options?.forEach((option, optionIndex) => {
      const key = `${groupIndex}-${optionIndex}`;
      totalSelected += selectedOptions[key] || 0;
    });
    
    return Math.max(0, freeQuantity - totalSelected);
  };

  // ✅ Check if option will be charged based on group's free quantity
  const willOptionBeCharged = (groupIndex, optionIndex) => {
    const group = service.optionGroups[groupIndex];
    const option = group.options[optionIndex];
    
    // Always free if marked as such
    if (option.isFree || group.allFree) return false;
    
    // Check if free quota is exceeded
    const freeRemaining = getFreeOptionsRemaining(groupIndex);
    return freeRemaining === 0;
  };

  const hasUnselectedRequiredGroups = () => {
    if (!service.optionGroups) return false;
    
    return service.optionGroups.some((group, groupIndex) => {
      if (!group.isRequired) return false;
      
      const hasSelection = group.options?.some((option, optionIndex) => {
        const key = `${groupIndex}-${optionIndex}`;
        return selectedOptions[key] > 0;
      });
      
      return !hasSelection;
    });
  };

  const selectedCount = Object.keys(selectedOptions).filter(key => selectedOptions[key] > 0).length;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900">{service.name}</h3>
            <p className="text-sm text-gray-600 mt-1">Customize your service</p>
            
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              {service.duration && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{service.duration}</span>
                </div>
              )}
              {service.deliveryMethod && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span className="capitalize">{service.deliveryMethod.replace('-', ' ')}</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-lg transition-colors ml-4"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Options List - BY GROUP WITH FREE QUANTITY LOGIC */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Base Service Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="font-semibold text-gray-900 mb-1">Base Service</div>
                <div className="text-sm text-gray-600">{service.description}</div>
              </div>
              <div className="text-right ml-4">
                <div className="text-2xl font-bold text-red-600">R{service.price}</div>
                {service.duration && (
                  <div className="text-xs text-gray-500">{service.duration}</div>
                )}
              </div>
            </div>
          </div>

          {/* RENDER OPTION GROUPS */}
          {service.optionGroups && service.optionGroups.length > 0 ? (
            service.optionGroups.map((group, groupIndex) => {
              const freeRemaining = getFreeOptionsRemaining(groupIndex);
              
              return (
                <div key={groupIndex} className="border-2 border-gray-200 rounded-xl p-5 space-y-4">
                  {/* Group Header */}
                  <div className="border-b border-gray-200 pb-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-lg font-bold text-gray-900">{group.name}</h4>
                        {group.isRequired && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                            Required
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {group.description && (
                      <p className="text-sm text-gray-600 mb-2">{group.description}</p>
                    )}
                    
                    {/* ✅ FREE QUANTITY INDICATOR */}
                    {group.allFree ? (
                      <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                        <Gift className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">
                          All options complimentary
                        </span>
                      </div>
                    ) : group.freeQuantity > 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                        <Gift className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-700">
                          {freeRemaining > 0 ? (
                            <>
                              <strong>{freeRemaining}</strong> free option{freeRemaining !== 1 ? 's' : ''} remaining 
                              <span className="text-blue-600"> (of {group.freeQuantity})</span>
                            </>
                          ) : (
                            <>All {group.freeQuantity} free options used - additional options will be charged</>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Options in Group */}
                  <div className="space-y-3">
                    {group.options && group.options.length > 0 ? (
                      group.options.map((option, optionIndex) => {
                        const key = `${groupIndex}-${optionIndex}`;
                        const quantity = selectedOptions[key] || 0;
                        const willBeCharged = willOptionBeCharged(groupIndex, optionIndex);
                        
                        return (
                          <div
                            key={optionIndex}
                            className={`border rounded-lg p-4 transition-all ${
                              quantity > 0 
                                ? 'border-blue-300 bg-blue-50' 
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h5 className="font-semibold text-gray-900">{option.name}</h5>
                                  {option.isRequired && (
                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                      Required
                                    </span>
                                  )}
                                  {quantity > 0 && (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  )}
                                </div>
                                {option.description && (
                                  <p className="text-sm text-gray-600">{option.description}</p>
                                )}
                              </div>
                              <div className="text-right ml-4">
                                {/* ✅ SHOW FREE OR CHARGED BASED ON GROUP LOGIC */}
                                {option.isFree || group.allFree || (!willBeCharged && freeRemaining > 0) ? (
                                  <span className="text-green-600 font-semibold">Free</span>
                                ) : (
                                  <span className="text-lg font-bold text-red-600">+R{option.price}</span>
                                )}
                              </div>
                            </div>

                            {/* Quantity Selector */}
                            {option.allowMultiple ? (
                              <div className="flex items-center gap-3">
                                <span className="text-sm text-gray-600 font-medium">Quantity:</span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => onOptionChange(key, Math.max(0, quantity - 1))}
                                    disabled={quantity === 0}
                                    className="w-8 h-8 rounded-lg border-2 border-gray-300 flex items-center justify-center hover:bg-gray-50 hover:border-blue-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                  <span className="w-12 text-center font-semibold text-lg">
                                    {quantity}
                                  </span>
                                  <button
                                    onClick={() => onOptionChange(key, quantity + 1)}
                                    disabled={quantity >= (option.maxQuantity || 999)}
                                    className="w-8 h-8 rounded-lg border-2 border-gray-300 flex items-center justify-center hover:bg-gray-50 hover:border-blue-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                </div>
                                {option.maxQuantity > 0 && (
                                  <span className="text-xs text-gray-500">
                                    (max {option.maxQuantity})
                                  </span>
                                )}
                              </div>
                            ) : (
                              <label className="flex items-center gap-2 cursor-pointer group">
                                <input
                                  type="checkbox"
                                  checked={quantity > 0}
                                  onChange={(e) => onOptionChange(key, e.target.checked ? 1 : 0)}
                                  className="sr-only"
                                />
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  quantity > 0 ? 'bg-blue-500 border-blue-500' : 'border-gray-300 group-hover:border-blue-400'
                                }`}>
                                  {quantity > 0 && <CheckCircle className="h-4 w-4 text-white" />}
                                </div>
                                <span className="text-sm text-gray-700 group-hover:text-gray-900">
                                  Add this option
                                </span>
                              </label>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-4 text-gray-500 text-sm">
                        No options in this group
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-gray-500">
              No option groups configured for this service
            </div>
          )}
        </div>

        {/* Footer - Price & Book Button */}
        <div className="border-t bg-gray-50 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm text-gray-600">Total Price</div>
              <div className="text-3xl font-bold text-red-600">R{calculateTotalPrice()}</div>
            </div>
            <div className="text-right text-sm text-gray-600">
              {selectedCount > 0 && (
                <span className="text-blue-600 font-medium">
                  {selectedCount} option{selectedCount !== 1 ? 's' : ''} selected
                </span>
              )}
            </div>
          </div>

          {hasUnselectedRequiredGroups() && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600">
                Please select at least one option from each required group
              </p>
            </div>
          )}

          <button
            onClick={onBook}
            disabled={hasUnselectedRequiredGroups()}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
            style={{
              background: hasUnselectedRequiredGroups() 
                ? undefined 
                : `linear-gradient(135deg, ${styling.primaryColor || '#3B82F6'}, ${styling.secondaryColor || '#2563EB'})`
            }}
          >
            <CheckCircle className="h-5 w-5" />
            Add to Bookings
          </button>
        </div>
      </div>
    </div>
  );
};